import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WS_SERVICE_FACTORY } from 'types';
import { CameraServiceBase } from '../../service/camera.service';
import { CameraServiceState, SizeInfo, CameraInfo, CameraType, HW_SERVICE_MOCK } from '../../service/hw-service-model';
import { CameraSettings, ComponentType } from '../cam.auto.connect';
import { AutoAction, BatteryAutoActionHelper } from '../auto.action';
import { HWServiceConfig } from '../../service/hw-service-config';
import { SystemService, SystemServiceFactory } from '../../service/system.service';
import { cameraServiceFactory } from '../../service/hw-service-factory';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'i-qrreader',
  templateUrl: './qrreader.component.html',
  styleUrls: ['./qrreader.component.scss'],
  host: { class: 'FLEX' },
  providers: [
    {
      provide: CameraServiceBase,
      useFactory: cameraServiceFactory,
      deps: [HW_SERVICE_MOCK, HWServiceConfig, WS_SERVICE_FACTORY, MatDialog],
    },
  ],
})
export class QrReaderComponent implements OnInit, OnDestroy {
  @Output() qrCodeData = new EventEmitter<any>();
  @Output() cancelScan = new EventEmitter<void>();

  private dead: ReplaySubject<void> = new ReplaySubject();
  private imageFitMode: string | null = null;
  private imageSrc: SafeUrl | null = null;
  public ligthsOn: boolean = false;
  private optimalResolution: number = 800 * 600;
  TIME: string;
  camList: CameraInfo[] = [];
  rotateToogle: boolean = true;
  ListSize: SizeInfo[];
  private autoCancel = new AutoAction(
    (text) => (this.TIME = text),
    () => this.cancel(),
  );
  private systemService: SystemService;
  private batteryHelper: BatteryAutoActionHelper;
  qrReaderState: QRReaderState = QRReaderState.Closed;

  public get imageSource$(): SafeUrl | null {
    return this.imageSrc;
  }

  public get imageFitMode$(): string | null {
    return this.imageFitMode;
  }

  constructor(
    private cameraService: CameraServiceBase,
    private settingsServiceFactory: SystemServiceFactory,
    private config: HWServiceConfig,
  ) {
    this.systemService = this.settingsServiceFactory.create(this.config);
    this.batteryHelper = new BatteryAutoActionHelper(this.autoCancel, this.systemService, this.dead);
  }

  ngOnInit(): void {
    this.cameraService.cameraState$.pipe(takeUntil(this.dead)).subscribe(
      (state) => this.onCameraStateChanged(state),
      (error) => this.handleError(error, QRReaderState.InitializationError),
    );
    this.qrReaderState = QRReaderState.InitializationInProgress;
    setTimeout(() => {
      this.cameraService.connect();
    }, 1);
    this.batteryHelper.run();
  }

  public canChangeLightMode(): boolean {
    let selectedCamera: CameraInfo = this.camList.find((ci) => ci.id === this.cameraService.cameraId$)!;
    return this.qrReaderState === QRReaderState.Preview && selectedCamera.type === CameraType.Back;
  }

  public changeLightMode() {
    let enable = !this.ligthsOn;
    this.setLightMode(enable);
    CameraSettings.setLightMode(ComponentType.QrReader, enable);
  }

  private setLightMode(enable: boolean) {
    this.ligthsOn = enable;
    this.cameraService.lights(enable);
  }

  private onCameraStateChanged(state: CameraServiceState): void {
    if (state === CameraServiceState.Ready) {
      this.cameraService
        .getCameraList()
        .pipe(takeUntil(this.dead))
        .subscribe(
          (list) => this.onCameraListReceived(list),
          (error) => this.handleError(error),
        );
    }
  }

  private onCameraListReceived(list: CameraInfo[]): void {
    this.camList = list;
    let selectedCamera = CameraSettings.selectCamera(ComponentType.QrReader, list, CameraType.Back);
    if (selectedCamera) {
      this.startQrScanning(selectedCamera);
    } else {
      this.handleError('Невозможно подключиться к QR сканеру', QRReaderState.InitializationError);
    }
  }

  rotate() {
    this.qrReaderState = QRReaderState.RotateCameraScreen;
    this.cameraService
      .stopPreview()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          let anotherCamera = CameraSettings.selectAnotherCamera(ComponentType.QrReader, this.camList, this.cameraService.cameraId$);
          if (anotherCamera) {
            this.rotateToogle = !this.rotateToogle;
            this.startQrScanning(anotherCamera);
          } else {
            this.handleError('Невозможно подключиться к QR сканеру', QRReaderState.InitializationError);
          }
        },
        (error) => this.handleError(error, QRReaderState.InitializationError),
      );
  }

  private startQrScanning(selectedCamera: CameraInfo) {
    this.cameraService.cameraId$ = selectedCamera.id;
    this.setLightMode(CameraSettings.getLightMode(ComponentType.QrReader, selectedCamera));
    let frame = CameraSettings.selectFrameByFormatThenArea(selectedCamera.frames, 'YUYV', this.optimalResolution);
    this.cameraService
      .scanQr(frame)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          if (r === undefined) {
            this.qrReaderState = QRReaderState.Preview;
          } else {
            this.qrCodeData.emit(r);
          }
        },
        (error) => this.handleError(error, QRReaderState.DecodeError),
      );
  }

  ngOnDestroy(): void {
    this.shutdownServices();
    this.dead.next();
    this.dead.complete();
  }

  public get state$(): string {
    return QRReaderState[this.qrReaderState];
  }
  public get rotateCameraScreen$(): boolean {
    return this.qrReaderState === QRReaderState.RotateCameraScreen;
  }
  public get isInitializationScreenVisible$(): boolean {
    return this.qrReaderState === QRReaderState.InitializationInProgress;
  }
  public get isInitializationErrorScreenVisible$(): boolean {
    return this.qrReaderState === QRReaderState.InitializationError;
  }
  public get isDecodeErrorScreenVisible$(): boolean {
    return this.qrReaderState === QRReaderState.DecodeError;
  }
  public get isShootingScreenVisible$(): boolean {
    return (
      this.qrReaderState !== QRReaderState.InitializationInProgress &&
      this.qrReaderState !== QRReaderState.InitializationError &&
      this.qrReaderState !== QRReaderState.DecodeError &&
      this.qrReaderState !== QRReaderState.ShootingResultConfirmation &&
      this.qrReaderState !== QRReaderState.ShutdownInProgress &&
      this.qrReaderState !== QRReaderState.Closed
    );
  }

  public cancel() {
    this.cancelScan.emit();
  }

  public shutdownServices(): void {
    this.cameraService.lights(false);
    this.cameraService.disconnect();
    this.systemService.disconnect().subscribe();
    this.dead.next();
    this.dead.complete();
  }

  private handleError(error: any, state: QRReaderState | null = null): void {
    console.error(error);
    if (state) {
      this.qrReaderState = state;
    }
  }
}

export enum QRReaderState {
  InitializationInProgress,
  InitializationError,
  DecodeError,
  Preview,
  ShootingInProgress,
  RotateCameraScreen,
  EyePositionSearch,
  EyePositionConfirmation,
  PhotoProcessingInProgress,
  ShootingResultConfirmation,
  ShutdownInProgress,
  Closed,
}
